import React, { useRef, useState, useEffect } from 'react';
import { ThemeProvider } from 'styled-components';
import styled from 'styled-components';
import { theme } from './theme';
import { GlobalStyles } from './GlobalStyles';
import { Header } from './components/Header';
import { Hero } from './components/Hero';
import { Services } from './components/Services';
import { About } from './components/About';
import { Features } from './components/Features';
import { AppointmentForm } from './components/AppointmentForm';
import { Testimonials } from './components/Testimonials';
import { Contact } from './components/Contact';
import { Footer } from './components/Footer';

const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(4px);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${({ theme }) => theme.spacing.md};
  animation: fadeIn 0.3s ease-out;
  
  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    padding: ${({ theme }) => theme.spacing.sm};
    align-items: flex-start;
    padding-top: 60px;
  }
`;

const ModalContent = styled.div`
  background: ${({ theme }) => theme.colors.white};
  border-radius: ${({ theme }) => theme.borderRadius.xl};
  padding: ${({ theme }) => theme.spacing.lg};
  max-width: 420px;
  width: 100%;
  position: relative;
  box-shadow: 0 25px 50px rgba(0, 0, 0, 0.3);
  animation: slideUp 0.4s ease-out;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  max-height: 90vh;
  overflow-y: auto;
  
  @keyframes slideUp {
    from {
      opacity: 0;
      transform: translateY(30px) scale(0.95);
    }
    to {
      opacity: 1;
      transform: translateY(0) scale(1);
    }
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    padding: ${({ theme }) => theme.spacing.md};
    max-width: 100%;
    border-radius: ${({ theme }) => theme.borderRadius.lg};
    max-height: calc(100vh - 80px);
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: ${({ theme }) => theme.spacing.md};
  right: ${({ theme }) => theme.spacing.md};
  width: 36px;
  height: 36px;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  background: rgba(0, 0, 0, 0.05);
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: ${({ theme }) => theme.fontSizes.xl};
  color: ${({ theme }) => theme.colors.darkGrey};
  transition: all ${({ theme }) => theme.transitions.fast};
  
  &:hover {
    background: rgba(0, 0, 0, 0.1);
    transform: rotate(90deg);
  }
`;

const OfferBadge = styled.div`
  background: linear-gradient(135deg, #10B981 0%, #059669 100%);
  color: white;
  padding: ${({ theme }) => theme.spacing.xs} ${({ theme }) => theme.spacing.md};
  border-radius: ${({ theme }) => theme.borderRadius.full};
  font-size: ${({ theme }) => theme.fontSizes.xs};
  font-weight: ${({ theme }) => theme.fontWeights.bold};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs};
  margin-bottom: ${({ theme }) => theme.spacing.sm};
  box-shadow: 0 4px 15px rgba(16, 185, 129, 0.4);
  animation: pulse 2s ease-in-out infinite;
  
  @keyframes pulse {
    0%, 100% {
      transform: scale(1);
      box-shadow: 0 4px 15px rgba(16, 185, 129, 0.4);
    }
    50% {
      transform: scale(1.05);
      box-shadow: 0 6px 25px rgba(16, 185, 129, 0.6);
    }
  }
  
  &::before {
    content: '🎁';
    font-size: ${({ theme }) => theme.fontSizes.sm};
  }
`;

const ModalHeader = styled.div`
  text-align: center;
  margin-bottom: ${({ theme }) => theme.spacing.md};
`;

const ModalIcon = styled.div`
  width: 48px;
  height: 48px;
  margin: 0 auto ${({ theme }) => theme.spacing.sm};
  background: linear-gradient(135deg, #0066CC, #00A8A8);
  border-radius: ${({ theme }) => theme.borderRadius.full};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: ${({ theme }) => theme.fontSizes['2xl']};
  box-shadow: 0 10px 30px rgba(0, 102, 204, 0.3);
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    width: 40px;
    height: 40px;
    font-size: ${({ theme }) => theme.fontSizes.xl};
  }
`;

const ModalTitle = styled.h2`
  color: #1e293b;
  font-size: ${({ theme }) => theme.fontSizes['2xl']};
  font-weight: ${({ theme }) => theme.fontWeights.bold};
  margin-bottom: ${({ theme }) => theme.spacing.xs};
  
  span {
    background: linear-gradient(135deg, #0066CC 0%, #00A8A8 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: ${({ theme }) => theme.fontSizes.xl};
  }
`;

const ModalDescription = styled.p`
  color: #64748b;
  font-size: ${({ theme }) => theme.fontSizes.xs};
  line-height: 1.4;
  margin-bottom: ${({ theme }) => theme.spacing.sm};
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    display: none;
  }
`;

const FeatureList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.xs};
  margin-bottom: ${({ theme }) => theme.spacing.md};
`;

const FeatureItem = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs};
  color: #475569;
  font-size: ${({ theme }) => theme.fontSizes.xs};
  
  &::before {
    content: '✓';
    display: flex;
    align-items: center;
    justify-content: center;
    width: 16px;
    height: 16px;
    background: linear-gradient(135deg, #0066CC 0%, #00A8A8 100%);
    border-radius: 50%;
    color: white;
    font-weight: bold;
    font-size: 10px;
    flex-shrink: 0;
  }
`;

const CTAButton = styled.button`
  width: 100%;
  background: linear-gradient(135deg, #0066CC 0%, #00A8A8 100%);
  color: ${({ theme }) => theme.colors.white};
  font-size: ${({ theme }) => theme.fontSizes.base};
  font-weight: ${({ theme }) => theme.fontWeights.semibold};
  padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.lg};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  border: none;
  cursor: pointer;
  transition: all ${({ theme }) => theme.transitions.base};
  box-shadow: 0 10px 30px rgba(0, 102, 204, 0.3);
  margin-bottom: ${({ theme }) => theme.spacing.sm};
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 15px 40px rgba(0, 102, 204, 0.4);
  }
  
  &:active {
    transform: translateY(0);
  }
`;

const SkipButton = styled.button`
  width: 100%;
  background: transparent;
  color: #64748b;
  font-size: ${({ theme }) => theme.fontSizes.sm};
  font-weight: ${({ theme }) => theme.fontWeights.medium};
  padding: ${({ theme }) => theme.spacing.sm};
  border: none;
  cursor: pointer;
  transition: color ${({ theme }) => theme.transitions.fast};
  
  &:hover {
    color: #1e293b;
  }
`;

function App() {
  const appointmentRef = useRef(null);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    // Show modal after a short delay when page loads
    const timer = setTimeout(() => {
      setShowModal(true);
    }, 1500);

    return () => clearTimeout(timer);
  }, []);

  const scrollToAppointment = () => {
    if (appointmentRef.current) {
      appointmentRef.current.scrollIntoView({ 
        behavior: 'smooth', 
        block: 'start' 
      });
    }
  };

  const handleBookConsultation = () => {
    setShowModal(false);
    setTimeout(() => {
      scrollToAppointment();
    }, 300);
  };

  const handleMaybeLater = () => {
    setShowModal(false);
  };

  const closeModal = () => {
    setShowModal(false);
  };

  return (
    <ThemeProvider theme={theme}>
      <GlobalStyles />
      <Header />
      <div id="home">
        <Hero onCTA={scrollToAppointment} />
      </div>
      <Services />
      <About />
      <Features />
      <AppointmentForm ref={appointmentRef} />
      <Testimonials />
      <Contact />
      <Footer />
      
      {showModal && (
        <ModalOverlay onClick={closeModal}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <CloseButton onClick={closeModal} aria-label="Close modal">
              ×
            </CloseButton>
            
            <ModalHeader>
              <OfferBadge>30 Days Free Trial Offer</OfferBadge>
              <ModalIcon>📋</ModalIcon>
              <ModalTitle>
                Free <span>Consultation</span>
              </ModalTitle>
              <ModalDescription>
                Discover how we can optimize your revenue cycle and increase your practice's profitability
              </ModalDescription>
            </ModalHeader>
            
            <FeatureList>
              <FeatureItem>30-minute expert consultation</FeatureItem>
              <FeatureItem>Full access to all RCM services for 30 days</FeatureItem>
              <FeatureItem>Custom strategy for your practice</FeatureItem>
              <FeatureItem>No credit card required</FeatureItem>
            </FeatureList>
            
            <CTAButton onClick={handleBookConsultation}>
              Book Free Consultation →
            </CTAButton>
            <SkipButton onClick={handleMaybeLater}>
              Maybe later
            </SkipButton>
          </ModalContent>
        </ModalOverlay>
      )}
    </ThemeProvider>
  );
}

export default App;
